import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';

// Backend expects: { "14": 90.0, "15": 93.0, ... }
// Keys = grades (as strings), Values = percentage thresholds (0-100)
// Semantics: LOWER inclusive bounds (backend picks highest grade where threshold <= achieved percentage)
export interface GradingScaleData {
  [grade: string]: number; // grade -> percentage threshold
}

export interface GradeEntry {
  grade: number;
  percentage: number;
}

export interface GradingScaleModalData {
  existingScale?: GradingScaleData;
  caseId?: string;
  caseName?: string;
  maxTotalScore?: number; // Sum of all focus point max_points
}

@Component({
  selector: 'app-grading-scale-modal',
  templateUrl: './grading-scale-modal.component.html',
  styleUrls: ['./grading-scale-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ]
})
export class GradingScaleModalComponent implements OnInit {
  public readonly bsModalRef = inject(BsModalRef);
  private readonly cdRef = inject(ChangeDetectorRef);

  // Data passed from parent component
  data: GradingScaleModalData = {
    existingScale: undefined,
    caseId: undefined,
    caseName: undefined,
    maxTotalScore: 0
  };

  // Result to be returned
  // undefined = cancelled, {} = delete custom scale, {...data} = save custom scale
  result: GradingScaleData | undefined = undefined;

  // UI state
  readonly grades = signal<GradeEntry[]>([]);
  readonly useCustomScale = signal<boolean>(false);

  ngOnInit() {
    // Always start with standard scale
    const standardScale = this.createStandardScale();
    
    if (this.data.existingScale && Object.keys(this.data.existingScale).length > 0) {
      // Load only percentages from existing scale, keep standard grades 0-18
      const existingPercentages = this.data.existingScale;
      const entries = standardScale.map(entry => ({
        grade: entry.grade,
        percentage: existingPercentages[String(entry.grade)] ?? entry.percentage
      }));
      this.grades.set(entries);
      this.useCustomScale.set(true);
    } else {
      // Start with standard scale as default
      this.grades.set(standardScale);
    }
  }

  // Standard 18-point scale (lower bounds)
  private createStandardScale(): GradeEntry[] {
    return [
      { grade: 0, percentage: 0 },
      { grade: 1, percentage: 5 },
      { grade: 2, percentage: 10 },
      { grade: 3, percentage: 15 },
      { grade: 4, percentage: 20 },
      { grade: 5, percentage: 25 },
      { grade: 6, percentage: 30 },
      { grade: 7, percentage: 35 },
      { grade: 8, percentage: 40 },
      { grade: 9, percentage: 45 },
      { grade: 10, percentage: 50 },
      { grade: 11, percentage: 55 },
      { grade: 12, percentage: 60 },
      { grade: 13, percentage: 65 },
      { grade: 14, percentage: 70 },
      { grade: 15, percentage: 75 },
      { grade: 16, percentage: 80 },
      { grade: 17, percentage: 85 },
      { grade: 18, percentage: 90 }
    ];
  }

  updateGrade(index: number, field: keyof GradeEntry, value: number) {
    // Only allow updating percentage, not grade number
    if (field !== 'percentage') {
      return;
    }
    
    const currentGrades = this.grades();
    currentGrades[index] = {
      ...currentGrades[index],
      [field]: value
    };
    this.grades.set([...currentGrades]);
  }

  // Calculate absolute points needed for each grade
  getAbsolutePoints(percentage: number): number {
    const maxTotal = this.data.maxTotalScore || 0;
    return (percentage / 100) * maxTotal;
  }

  toggleCustomScale() {
    this.useCustomScale.update(v => !v);
    if (!this.useCustomScale()) {
      // Reset to standard scale when disabled
      this.grades.set(this.createStandardScale());
    }
  }

  save() {
    if (!this.useCustomScale()) {
      // User doesn't want custom scale - use empty object to indicate deletion
      this.result = {} as GradingScaleData;
    } else {
      // Convert to backend format: { "14": 90.0, "15": 93.0, ... }
      const gradeEntries = this.grades();
      
      // Validate standard scale has all grades 0-18
      if (gradeEntries.length !== 19) {
        alert('Fehler: Die Standard-Notenskala muss alle Noten von 0 bis 18 enthalten.');
        return;
      }

      // Already sorted since we use standard scale
      const sorted = gradeEntries;
      
      // Check monotonic increasing thresholds
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].percentage <= sorted[i-1].percentage) {
          alert(`Fehler: Die Prozentsätze müssen mit steigender Note zunehmen.\nNote ${sorted[i-1].grade} (${sorted[i-1].percentage}%) -> Note ${sorted[i].grade} (${sorted[i].percentage}%) ist nicht aufsteigend.`);
          return;
        }
      }

      // Convert to backend format
      const result: GradingScaleData = {};
      for (const entry of sorted) {
        result[String(entry.grade)] = entry.percentage;
      }
      
      this.result = result;
    }
    this.bsModalRef.hide();
  }

  cancel() {
    this.result = undefined;
    this.bsModalRef.hide();
  }

  skipForNow() {
    // Skip and don't set custom scale
    this.result = undefined;
    this.bsModalRef.hide();
  }
}

